/**
 /**
 * CartBaseStoreManager - Manages cart to baseStore mapping
 * Handles cross-tab synchronization and prevents base store conflicts
 */

const STORAGE_KEY = 'cart_basestore_map';
const ACTIVE_CART_KEY = 'active_cart';
const ACTIVE_BASESTORE_KEY = 'active_basestore';
const CART_SYNC_EVENT = 'cart_basestore_sync';

class CartBaseStoreManager {
  constructor() {
    this.cartBaseStoreMap = new Map();
    this.activeCart = null;
    this.activeBaseStore = null;
    this.listeners = [];
    this.init();
  }

  init() {
    if (typeof window === 'undefined') return;
    
    // Load from storage
    this.loadFromStorage();
    
    // Listen for storage changes from other tabs
    window.addEventListener('storage', (e) => this.handleStorageChange(e));
    
    // Listen for custom sync events
    window.addEventListener(CART_SYNC_EVENT, (e) => this.handleSyncEvent(e));
  }

  /**
   * Register a cart with its base store
   * @param {string} cartId - Cart identifier
   * @param {string} baseStore - Base store/port (e.g., 'singapore', 'japan')
   */
  registerCart(cartId, baseStore) {
    if (!cartId || !baseStore) {
      console.warn('⚠️  CartBaseStoreManager: Invalid cartId or baseStore');
      return false;
    }

    const previousStore = this.cartBaseStoreMap.get(cartId);
    
    // Check if this cart already exists with a different baseStore
    if (previousStore && previousStore !== baseStore) {
      console.warn(
        `⚠️  CartBaseStoreManager: Cart ${cartId} baseStore changed from ${previousStore} to ${baseStore}`
      );
      // Invalidate cache for this cart
      this.invalidateCartCache(cartId);
    }

    this.cartBaseStoreMap.set(cartId, baseStore);
    this.saveToStorage();
    this.notifyListeners('cartRegistered', { cartId, baseStore });
    
    console.log(`✅ CartBaseStoreManager: Cart ${cartId} registered with baseStore ${baseStore}`);
    return true;
  }

  /**
   * Set the active cart and baseStore
   * @param {string} cartId - Cart identifier
   * @param {string} baseStore - Base store/port
   */
  setActiveCart(cartId, baseStore) {
    const registeredStore = this.cartBaseStoreMap.get(cartId);
    
    if (registeredStore && registeredStore !== baseStore) {
      console.error(
        `❌ CartBaseStoreManager: BaseStore mismatch for cart ${cartId}. Expected ${registeredStore}, got ${baseStore}`
      );
      return false;
    }

    this.activeCart = cartId;
    this.activeBaseStore = baseStore;
    this.saveToStorage();
    this.broadcastSync();
    this.notifyListeners('activeCartChanged', { cartId, baseStore });
    
    console.log(`✅ CartBaseStoreManager: Active cart set to ${cartId} with baseStore ${baseStore}`);
    return true;
  }

  /**
   * Get the registered baseStore for a cart
   * @param {string} cartId - Cart identifier
   * @returns {string|null} - BaseStore or null if not found
   */
  getCartBaseStore(cartId) {
    return this.cartBaseStoreMap.get(cartId) || null;
  }

  /**
   * Verify baseStore matches registered cart
   * @param {string} cartId - Cart identifier
   * @param {string} baseStore - Base store to verify
   * @returns {boolean} - True if baseStore matches
   */
  verifyBaseStore(cartId, baseStore) {
    const registeredStore = this.getCartBaseStore(cartId);
    if (!registeredStore) {
      console.warn(`⚠️  CartBaseStoreManager: Cart ${cartId} not found in registry`);
      return false;
    }
    
    const isValid = registeredStore === baseStore;
    if (!isValid) {
      console.error(
        `❌ CartBaseStoreManager: BaseStore mismatch for cart ${cartId}. Expected ${registeredStore}, got ${baseStore}`
      );
    }
    return isValid;
  }

  /**
   * Get active cart info
   * @returns {Object} - { cartId, baseStore }
   */
  getActiveCart() {
    return {
      cartId: this.activeCart,
      baseStore: this.activeBaseStore
    };
  }

  /**
   * Check if baseStore has changed for a cart
   * @param {string} cartId - Cart identifier
   * @param {string} previousBaseStore - Previous baseStore
   * @returns {boolean} - True if baseStore changed
   */
  hasBaseStoreChanged(cartId, previousBaseStore) {
    const currentStore = this.getCartBaseStore(cartId);
    return currentStore && currentStore !== previousBaseStore;
  }

  /**
   * Get all carts for a baseStore
   * @param {string} baseStore - Base store/port
   * @returns {Array<string>} - Array of cart IDs
   */
  getCartsForBaseStore(baseStore) {
    const carts = [];
    for (const [cartId, store] of this.cartBaseStoreMap.entries()) {
      if (store === baseStore) {
        carts.push(cartId);
      }
    }
    return carts;
  }

  /**
   * Invalidate cache for a specific cart
   * @param {string} cartId - Cart identifier
   */
  invalidateCartCache(cartId) {
    if (typeof window === 'undefined') return;
    
    // Emit event to notify subscribers to refresh data
    const event = new CustomEvent('cartCacheInvalidated', {
      detail: { cartId }
    });
    window.dispatchEvent(event);
    
    this.notifyListeners('cacheInvalidated', { cartId });
    console.log(`🔄 CartBaseStoreManager: Cache invalidated for cart ${cartId}`);
  }

  /**
   * Unregister a cart from the baseStore registry
   * @param {string} cartId - Cart identifier
   * @returns {boolean} - True when cart was removed
   */
  unregisterCart(cartId) {
    if (!cartId) {
      return false;
    }

    const existed = this.cartBaseStoreMap.delete(cartId);
    if (!existed) {
      return false;
    }

    if (this.activeCart === cartId) {
      this.activeCart = null;
      this.activeBaseStore = 'uk';
    }

    this.saveToStorage();
    this.notifyListeners('cartUnregistered', { cartId });
    console.log(`✅ CartBaseStoreManager: Cart ${cartId} unregistered`);
    return true;
  }

  /**
   * Add listener for cart/baseStore changes
   * @param {Function} callback - Callback function
   */
  onChanged(callback) {
    if (typeof callback === 'function') {
      this.listeners.push(callback);
    }
  }

  /**
   * Remove listener
   * @param {Function} callback - Callback function
   */
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners
   * @private
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in CartBaseStoreManager listener:', error);
      }
    });
  }

  /**
   * Save state to storage
   * @private
   */
  saveToStorage() {
    if (typeof window === 'undefined') return;

    try {
      const mapObject = Object.fromEntries(this.cartBaseStoreMap);
      // Persist cart ↔ baseStore map to localStorage for cross-tab access
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(mapObject));
      // Keep active cart/baseStore per-tab in sessionStorage
      window.sessionStorage.setItem(ACTIVE_CART_KEY, this.activeCart || '');
      window.sessionStorage.setItem(ACTIVE_BASESTORE_KEY, this.activeBaseStore || '');
    } catch (error) {
      console.error('Error saving CartBaseStoreManager state:', error);
    }
  }

  /**
   * Load state from storage
   * @private
   */
  loadFromStorage() {
    if (typeof window === 'undefined') return;

    try {
      const mapData = window.localStorage.getItem(STORAGE_KEY) || window.sessionStorage.getItem(STORAGE_KEY);
      if (mapData) {
        const mapObject = JSON.parse(mapData);
        this.cartBaseStoreMap = new Map(Object.entries(mapObject));
      }

      this.activeCart = window.sessionStorage.getItem(ACTIVE_CART_KEY) || null;
      this.activeBaseStore = window.sessionStorage.getItem(ACTIVE_BASESTORE_KEY) || null;
    } catch (error) {
      console.error('Error loading CartBaseStoreManager state:', error);
    }
  }

  /**
   * Broadcast sync event to other tabs
   * @private
   */
  broadcastSync() {
    if (typeof window === 'undefined') return;
    
    const event = new CustomEvent(CART_SYNC_EVENT, {
      detail: {
        cartMap: Object.fromEntries(this.cartBaseStoreMap),
        activeCart: this.activeCart,
        activeBaseStore: this.activeBaseStore
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Handle storage change from other tabs
   * @private
   */
  handleStorageChange(e) {
    if (e.key === STORAGE_KEY) {
      this.loadFromStorage();
      this.notifyListeners('storageSynced', {
        activeCart: this.activeCart,
        activeBaseStore: this.activeBaseStore
      });
    }
  }

  /**
   * Handle sync event from other tabs
   * @private
   */
  handleSyncEvent(e) {
    const { cartMap, activeCart, activeBaseStore } = e.detail;
    
    // Update cart map if changed
    if (cartMap) {
      this.cartBaseStoreMap = new Map(Object.entries(cartMap));
    }
    
    // Update active cart if different
    if (activeCart && activeCart !== this.activeCart) {
      console.log(`🔄 CartBaseStoreManager: Synced from other tab. Active cart: ${activeCart}`);
      this.activeCart = activeCart;
      this.activeBaseStore = activeBaseStore;
      this.notifyListeners('syncedFromTab', {
        activeCart,
        activeBaseStore
      });
    }
  }

  /**
   * Clear all data
   */
  clear() {
    this.cartBaseStoreMap.clear();
    this.activeCart = null;
    this.activeBaseStore = null;
    this.saveToStorage();
    this.notifyListeners('cleared', {});
  }
}

// Singleton instance
const cartBaseStoreManager = new CartBaseStoreManager();

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__CART_BASESTORE_MANAGER__ = cartBaseStoreManager;
}

export default cartBaseStoreManager;
